import { Sentiment } from '../central/constants/sentiment';
import type { TSentimentBuckets } from '../central/types/intelligence.type';
import { resolveDominantSentiment } from './sentiment.utils';

// Shared by the Home v2 What's changed section and the Weekly Intelligence Brief
// email, so a movement reads the same in both.

// * One rule for the whole Intelligence section: a topic's sentiment is its
// * biggest bucket (ties unhappy, then happy - see resolveDominantSentiment), and
// * its percentage is that bucket's share of ALL the topic's questions, including
// * the ones not classified yet.
export type DominantSentimentShare = {
  sentiment: Sentiment;
  count: number;
  // 0..100, whole number: count / volume. Capped at 100 so inconsistent counts
  // (a bucket briefly above the volume) never print more than 100%.
  percent: number;
};

export const resolveDominantShare = (
  buckets: TSentimentBuckets,
  volume: number,
): DominantSentimentShare | null => {
  const sentiment = resolveDominantSentiment(buckets);
  if (!sentiment || volume <= 0) {
    return null;
  }
  const count = buckets[sentiment];
  return {
    sentiment,
    count,
    percent: Math.min(100, Math.round((count / volume) * 100)),
  };
};

// The fields the movers read - both the Home v2 What's changed DTO and Core's
// topic comparison entry carry them.
export type TComparableTopic = {
  currentVolume: number;
  priorVolume: number;
  currentSentiment: TSentimentBuckets;
  priorSentiment: TSentimentBuckets;
};

// How many movers each column shows.
const SENTIMENT_MOVERS_LIMIT = 3;
const VOLUME_MOVERS_LIMIT = 3;
const NEW_FADING_LIMIT = 3;

// Ordinal rank of each level so "improving" vs "deteriorating" is a simple comparison.
const SENTIMENT_RANK: Record<Sentiment, number> = {
  [Sentiment.Unhappy]: 0,
  [Sentiment.Neutral]: 1,
  [Sentiment.Happy]: 2,
};

export type TSentimentMover<TTopic extends TComparableTopic> = {
  topic: TTopic;
  fromLevel: Sentiment;
  toLevel: Sentiment;
  // How many percentage points the new biggest bucket's share of all the topic's
  // questions moved between the two periods. Signed: usually positive, but it is
  // negative when volume grew with unclassified rows, so the bucket leads while
  // its share fell - such a topic sorts last.
  delta: number;
};

export type TSentimentMovers<TTopic extends TComparableTopic> = {
  improving: TSentimentMover<TTopic>[];
  deteriorating: TSentimentMover<TTopic>[];
};

/**
 * Splits topics into improving vs deteriorating by a change of their biggest
 * sentiment bucket between the prior and current windows - the same rule the
 * table colours by (resolveDominantShare). Only topics with classified questions
 * in BOTH periods are judged, and only a change of the biggest bucket counts as a
 * move. Each column is sorted by how far the new biggest bucket's share moved,
 * largest first, and capped.
 */
export const deriveSentimentMovers = <TTopic extends TComparableTopic>(
  topics: TTopic[],
): TSentimentMovers<TTopic> => {
  const improving: TSentimentMover<TTopic>[] = [];
  const deteriorating: TSentimentMover<TTopic>[] = [];

  for (const topic of topics) {
    const prior = resolveDominantShare(topic.priorSentiment, topic.priorVolume);
    const current = resolveDominantShare(
      topic.currentSentiment,
      topic.currentVolume,
    );
    if (!prior || !current || prior.sentiment === current.sentiment) {
      continue;
    }
    const priorShareOfNewLevel = Math.round(
      (topic.priorSentiment[current.sentiment] / topic.priorVolume) * 100,
    );
    const mover: TSentimentMover<TTopic> = {
      topic,
      fromLevel: prior.sentiment,
      toLevel: current.sentiment,
      delta: current.percent - priorShareOfNewLevel,
    };
    if (SENTIMENT_RANK[current.sentiment] > SENTIMENT_RANK[prior.sentiment]) {
      improving.push(mover);
    } else {
      deteriorating.push(mover);
    }
  }

  improving.sort((first, second) => second.delta - first.delta);
  deteriorating.sort((first, second) => second.delta - first.delta);

  return {
    improving: improving.slice(0, SENTIMENT_MOVERS_LIMIT),
    deteriorating: deteriorating.slice(0, SENTIMENT_MOVERS_LIMIT),
  };
};

export type TVolumeMover<TTopic extends TComparableTopic> = {
  topic: TTopic;
  // currentVolume - priorVolume.
  delta: number;
  // Percentage change vs the prior window (+100 for a topic that emerged from zero).
  pctChange: number;
  isNew: boolean;
};

export type TVolumeMovers<TTopic extends TComparableTopic> = {
  rising: TVolumeMover<TTopic>[];
  falling: TVolumeMover<TTopic>[];
};

// A topic emerging from zero has no defined percentage base; treat it as +100%.
const computePctChange = (current: number, prior: number): number => {
  if (prior === 0) {
    return current > 0 ? 100 : 0;
  }
  return ((current - prior) / prior) * 100;
};

/**
 * Splits topics into rising vs falling by question-volume change, sorted by the absolute
 * size of the change (biggest movers first) and capped.
 */
export const deriveVolumeMovers = <TTopic extends TComparableTopic>(
  topics: TTopic[],
): TVolumeMovers<TTopic> => {
  const rising: TVolumeMover<TTopic>[] = [];
  const falling: TVolumeMover<TTopic>[] = [];

  for (const topic of topics) {
    const delta = topic.currentVolume - topic.priorVolume;
    if (delta === 0) {
      continue;
    }
    const mover: TVolumeMover<TTopic> = {
      topic,
      delta,
      pctChange: computePctChange(topic.currentVolume, topic.priorVolume),
      isNew: topic.priorVolume === 0 && topic.currentVolume > 0,
    };
    if (delta > 0) {
      rising.push(mover);
    } else {
      falling.push(mover);
    }
  }

  rising.sort(
    (first, second) => Math.abs(second.delta) - Math.abs(first.delta),
  );
  falling.sort(
    (first, second) => Math.abs(second.delta) - Math.abs(first.delta),
  );

  return {
    rising: rising.slice(0, VOLUME_MOVERS_LIMIT),
    falling: falling.slice(0, VOLUME_MOVERS_LIMIT),
  };
};

export type TNewAndFading<TTopic extends TComparableTopic> = {
  newTopics: TTopic[];
  fadingTopics: TTopic[];
};

/**
 * New = topics with questions this window but none in the prior window (emerged).
 * Fading = topics that had questions in the prior window and dropped to zero this window (fully gone).
 */
export const deriveNewAndFading = <TTopic extends TComparableTopic>(
  topics: TTopic[],
): TNewAndFading<TTopic> => {
  const newTopics = topics
    .filter((topic) => topic.priorVolume === 0 && topic.currentVolume > 0)
    .sort((first, second) => second.currentVolume - first.currentVolume)
    .slice(0, NEW_FADING_LIMIT);

  const fadingTopics = topics
    .filter((topic) => topic.currentVolume === 0 && topic.priorVolume > 0)
    .sort((first, second) => second.priorVolume - first.priorVolume)
    .slice(0, NEW_FADING_LIMIT);

  return { newTopics, fadingTopics };
};
